import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, SongAlbumCard, message } from '@common/musicHubCommon';
import { ROUTES } from '../../../routes';
import { getUserData, useGenreStore } from '../../../store';
import { reportPageView } from '../../../services/reportLog';
import './index.scss';

interface BasicInfo {
  songTitle: string;
  coverArtPreview: string | null;
  featuredArtists: string[];
  producer: string;
  genres: string[];
  moods: string[];
}

// 社交分享图标组件
const WhatsAppIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const FacebookIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const XIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const LinkIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const ReleaseSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [copySuccess, setCopySuccess] = useState(false);
  
  // 从 store 获取 genre 映射
  const genreOptions = useGenreStore((state) => state.genreOptions);
  const getGenreLabel = useCallback((id: string) => {
    const option = genreOptions.find(g => g.value === id);
    return option?.label || id;
  }, [genreOptions]);

  // 从 sessionStorage 获取基本信息
  const basicInfo: BasicInfo | null = useMemo(() => {
    const stored = sessionStorage.getItem('releaseBasicInfo');
    return stored ? JSON.parse(stored) : null;
  }, []);

  useEffect(() => {
    reportPageView('content_0018', { withArtistId: true });
  }, []);

  // 检查是否有基本信息
  useEffect(() => {
    if (!basicInfo) {
      navigate(ROUTES.dashboard);
    }
  }, [basicInfo, navigate]);

  // 清理 sessionStorage
  useEffect(() => {
    return () => {
      // 页面离开时清理
      sessionStorage.removeItem('releaseType');
      sessionStorage.removeItem('releaseAudioFile');
      sessionStorage.removeItem('releaseBasicInfo');
      sessionStorage.removeItem('releaseAdvancedInfo');
      sessionStorage.removeItem('releaseScheduleInfo');
    };
  }, []);

  // 处理返回
  const handleBack = () => {
    navigate(ROUTES.dashboard);
  };

  // 处理 "Go to My Songs"
  const handleGoToMySongs = () => {
    navigate(ROUTES.mySongs);
  };

  // 获取艺术家名称（从用户信息）
  const artistName = getUserData()?.name || '';

  // 分享功能
  const handleShareWhatsApp = () => {
    const text = `Check out my new song "${basicInfo?.songTitle}" by ${artistName}!`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  const handleShareFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.origin)}`;
    window.open(url, '_blank');
  };

  const handleShareX = () => {
    const text = `Check out my new song "${basicInfo?.songTitle}" by ${artistName}!`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin);
      setCopySuccess(true);
      message.success('Link copied!');
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      message.error('Failed to copy link');
    }
  };

  if (!basicInfo) {
    return null;
  }

  // 将 genre ID 映射为 label
  const validGenres = basicInfo.genres.filter(g => g.trim() !== '').map(getGenreLabel);

  return (
    <div className="release-success">
      {/* 页面标题 */}
      <div className="release-success__header">
        <button className="back-btn" onClick={handleBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
          <span>Release Song</span>
        </button>
      </div>

      {/* 主内容 */}
      <div className="release-success__content">
        <div className="release-success__card">
          {/* 成功标题 */}
          <div className="release-success__heading">
            <h1>Song submitted for release</h1>
            <p>It will be officially released after review.</p>
          </div>

          {/* 歌曲预览卡片 */}
          <div className="song-preview">
            <SongAlbumCard
              coverUrl={basicInfo.coverArtPreview || undefined}
              title={basicInfo.songTitle}
              artist={artistName}
              tags={validGenres}
            />
          </div>

          {/* 分享区域 */}
          <div className="share-section">
            <h3 className="share-section__title">Share Your Song</h3>
            <div className="share-section__buttons">
              {/* WhatsApp */}
              <button className="share-btn share-btn--whatsapp" onClick={handleShareWhatsApp}>
                <div className="share-btn__icon">
                  <WhatsAppIcon />
                </div>
                <span className="share-btn__label">WhatsApp</span>
              </button>

              {/* Facebook */}
              <button className="share-btn share-btn--facebook" onClick={handleShareFacebook}>
                <div className="share-btn__icon">
                  <FacebookIcon />
                </div>
                <span className="share-btn__label">Facebook</span>
              </button>

              {/* X (Twitter) */}
              <button className="share-btn share-btn--x" onClick={handleShareX}>
                <div className="share-btn__icon">
                  <XIcon />
                </div>
                <span className="share-btn__label">X</span>
              </button>

              {/* Copy Link */}
              <button className="share-btn share-btn--link" onClick={handleCopyLink}>
                <div className="share-btn__icon">
                  <LinkIcon />
                </div>
                <span className="share-btn__label">{copySuccess ? 'Copied!' : 'Copy Link'}</span>
              </button>
            </div>
          </div>

          {/* Go to My Songs 按钮 */}
          <Button
            type="primary"
            onClick={handleGoToMySongs}
            className="release-success__submit"
          >
            Go to My Songs
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReleaseSuccess;
